export const pagesSalaryAndWagesEn = {
  alerts: {
    actions: {
      exportToastDescription: 'Export is not available in the demo.',
      exportToastTitle: 'Export'
    },
    columns: {
      affectedPeriod: 'Affected period',
      alert: 'Alert',
      detectedAt: 'Detected',
      dueDate: 'Due',
      member: 'Member',
      organization: 'Organization',
      payrollStructure: 'Payroll structure',
      severity: 'Severity'
    },
    detail: {
      affectedPeriod: 'Affected period',
      assumptions: 'Assumptions',
      baselineValue: 'Baseline',
      close: 'Close',
      criteria: 'Criteria',
      currentValue: 'Current',
      detectedAt: 'Detected',
      directReason: 'Why this alert fired',
      dueOverdue_one: '{{count}} day overdue',
      dueOverdue_other: '{{count}} days overdue',
      dueRemaining_one: '{{count}} day left',
      dueRemaining_other: '{{count}} days left',
      dueToday: 'Due today',
      escalation: 'Escalation and resolution',
      establishment: 'Establishment',
      forecastConfidence: 'Forecast confidence',
      forecastValue: 'Forecast',
      formula: 'Formula',
      judgedPeriods: 'Pay periods used',
      metrics: 'Details to review',
      nextEvaluationAt: 'Next evaluation',
      organization: 'Organization',
      payrollStructure: 'Payroll structure',
      purpose: 'What this alert tells you',
      recommendedActions: 'Recommended actions',
      requiredData: 'Data used',
      resolvedAt: 'Resolved at',
      ruleVersion: 'Rule version',
      sections: {
        about: 'About this alert',
        judgment: 'Basis of judgment',
        target: 'Subject and period',
        values: 'Current, baseline and forecast'
      },
      subject: 'Subject',
      thresholds: 'Thresholds',
      triggerTimings: 'Trigger timings'
    },
    empty: 'No alerts match the current filters.',
    error: 'Failed to load alerts.',
    items: {
      minimumWageShortfall: 'Minimum wage shortfall expected',
      negativeNetPay: 'Negative net pay or uncollectible deduction expected',
      netPayDrop: 'Large net pay decrease expected',
      payrollBudgetOverrun: 'Payroll total or funding over budget',
      payrollItemExpiry: 'Earning or deduction item expiring',
      retroactiveCalculation: 'Retroactive calculation required',
      socialInsuranceRevision: 'Possible social insurance revision (monthly remuneration change)',
      yearEndAdjustmentDelta: 'Large year-end adjustment shortfall or refund expected'
    },
    organizationScope: 'Organization-wide',
    resolvedBadge: 'Resolved',
    searchPlaceholder: 'Search by subject, organization or alert',
    severity: {
      error: 'Error',
      warning: 'Warning'
    },
    severityDescription: {
      error: 'Finalized information, including future-dated changes, makes a legal violation, unpaid amount, payment shortfall or missed filing a reasonable conclusion. Finalizing or paying the target month while this is open is not recommended.',
      warning: 'Past trends or unconfirmed future information indicate that action may become necessary. Processing need not stop, but review, planning and employee notice are recommended.'
    },
    title: 'Alerts',
    valueKind: {
      confirmed: 'Confirmed',
      estimated: 'Estimated',
      scheduled: 'Scheduled'
    }
  },
  analysis: {
    chart: {
      ageComparison: {
        helpTooltip: 'Average base salary, gross pay, and median by age group for the selected analysis period.',
        title: 'Compensation by age group'
      },
      leavePayForecast: {
        helpTooltip: 'Monthly and cumulative pay forecasts for employees on leave of absence.',
        title: 'Leave-of-absence pay forecast'
      },
      list: {
        helpTooltip: 'Number of saved analyses grouped by analysis type.',
        series: {
          count: 'Count'
        },
        title: 'Analyses by type'
      },
      raiseImpact: {
        helpTooltip: 'Budgeted vs actual spend for each raise measure in the selected analysis.',
        title: 'Raise measure impact'
      }
    },
    columns: {
      ageGroup: 'Age Group',
      analysisName: 'Analysis Name',
      author: 'Author',
      avgBase: 'Avg. Base Salary',
      avgGross: 'Avg. Gross Pay',
      budget: 'Budget',
      cumulativeForecast: 'Cumulative Forecast',
      department: 'Department',
      effect: 'Effect',
      employeeName: 'Employee',
      expectedReturn: 'Expected Return',
      headcount: 'Headcount',
      leaveType: 'Leave Type',
      measure: 'Measure',
      median: 'Median',
      monthlyForecast: 'Monthly Forecast',
      result: 'Actual',
      startDate: 'Start Date',
      status: 'Status',
      targets: 'Targets',
      type: 'Type',
      updatedAt: 'Updated',
      variance: 'Variance',
      yoy: 'YoY'
    },
    empty: 'No analysis data.',
    list: 'Analysis List',
    recent: {
      ageComparison: 'Jun 2025 — Age Group Comparison',
      leavePayForecast: 'Leave-of-Absence Pay Forecast',
      raiseImpact: 'Raise Measure Impact',
      title: 'Recent'
    },
    showSummary: 'Show summary',
    startNew: 'Start new',
    title: 'Analysis'
  },
  contractAndCost: {
    chart: {
      cost: {
        helpTooltip: 'Monthly gross pay, employer contributions, and total labor cost, based on payment date.',
        title: 'Labor cost trend'
      },
      payment: {
        helpTooltip: 'Monthly gross pay, deductions, and net pay, based on payment date.',
        title: 'Pay trend'
      }
    },
    empty: {
      subtitle: 'No pay was recorded in the selected 12-month window.',
      title: 'No records for this period'
    },
    grid: {
      entityHeader: {
        department: 'Department',
        member: 'Employee'
      },
      item: 'Item',
      loadError: 'Failed to load.',
      total: 'Total',
      trend: 'Trend'
    },
    rows: {
      employerContribution: 'Employer',
      grossAmount: 'Gross',
      netAmount: 'Net pay',
      totalDeductions: 'Deductions',
      totalLaborCost: 'Labor cost'
    },
    statCards: {
      comparison: {
        PREVIOUS_MONTH: 'MoM',
        PREVIOUS_YEAR: 'YoY'
      },
      cost: {
        employerContribution: 'Employer contributions',
        employerContributionTooltip: "The employer's share of social insurance and other statutory contributions.",
        grossAmount: 'Gross pay',
        grossAmountTooltip: 'Total earnings before deductions, including bonuses.',
        periodTotal: 'Labor cost (12 months)',
        totalLaborCost: 'Total labor cost',
        totalLaborCostTooltip: 'Gross pay plus employer contributions — what the company actually bears.'
      },
      headcount: '{{count}} employees',
      latestMonth: 'As of {{month}}',
      noData: '–',
      payment: {
        grossAmount: 'Gross pay',
        grossAmountTooltip: 'Total earnings before deductions, including bonuses.',
        netAmount: 'Net pay',
        netAmountTooltip: 'Gross pay minus deductions — the amount actually paid to employees.',
        periodTotal: 'Net pay (12 months)',
        totalDeductions: 'Deductions',
        totalDeductionsTooltip: 'Statutory deductions (tax, social insurance) plus agreed deductions.'
      }
    }
  },
  employeeDetail: {
    bonusEmpty: 'No bonus pay slips for this employee.',
    empty: 'No pay slips for this employee.',
    files: {
      actions: {
        back: 'Back',
        delete: 'Delete',
        deleteToastDescription: 'Delete is not available in the demo.',
        deleteToastTitle: 'Delete',
        download: 'Download',
        downloadToastDescription: 'Download is not available in the demo.',
        downloadToastTitle: 'Download',
        openFolder: 'Open {{folderName}}',
        previewToastDescription: 'Preview is not available in the demo.',
        previewToastTitle: 'Preview',
        rename: 'Rename',
        renameToastDescription: 'Rename is not available in the demo.',
        renameToastTitle: 'Rename'
      },
      columns: {
        fileCount: 'Files',
        fileName: 'File name',
        folder: 'Folder',
        updatedAt: 'Updated'
      },
      folders: {
        bonus: 'Bonus statements',
        payslip: 'Pay slips',
        yearEndAdjustment: 'Year-end adjustment'
      },
      title: 'Files'
    },
    history: {
      addComparison: 'Add comparison',
      categories: {
        attendance: 'Attendance & basis',
        earnings: 'Earnings',
        result: 'Results',
        social_insurance: 'Social insurance',
        tax: 'Tax'
      },
      collapsePayItemLines: 'Collapse breakdown for {{label}}',
      columnLabels: {
        variable: 'Variable / category'
      },
      currentBadge: 'Current',
      emptyNoSnapshots: 'No calculation history for this month.',
      expandPayItemLines: 'Expand breakdown for {{label}}',
      records: {
        empty: 'No pay slip history for this employee.'
      },
      searchAriaLabel: 'Search calculation history',
      searchPlaceholder: 'Find history…',
      showChangesOnly: 'Show only changes',
      timeline: {
        attributes: {
          calcLogicVersion: 'Calculation logic version',
          finalizedBy: 'Finalized by',
          lockFlag: 'Lock status',
          payPeriod: 'Pay period',
          recordNumber: 'Record number',
          revisionNumber: 'Revision number',
          runType: 'Pay run type',
          source: 'Source',
          status: 'Status'
        },
        eventTypeWithRunType: '{{eventType}} ({{runType}})',
        eventTypes: {
          creation: 'Pay slip created',
          finalization: 'Pay slip finalized',
          lastUpdate: 'Last updated'
        },
        lockFlagValue: {
          locked: 'Locked',
          unlocked: 'Unlocked'
        }
      }
    },
    tabs: {
      alerts: 'Alerts',
      bonusPaySlipList: 'Bonus pay slips',
      files: 'Files',
      history: 'History',
      overview: 'Overview',
      paySlipList: 'Pay slips',
      payslip: 'Pay slip',
      settings: 'Settings',
      wageLedger: 'Wage ledger',
      withholdingSlipList: 'Withholding slips'
    },
    tabsAriaLabel: 'Employee pay slip sections',
    toolbar: {
      compare: 'Compare',
      compareToastDescription: 'Comparison is not available in the demo.',
      compareToastTitle: 'Compare',
      download: 'Download',
      downloadToastDescription: 'Download is not available in the demo.',
      downloadToastTitle: 'Download',
      noResults: 'No pay slips match the search.',
      searchPlaceholder: 'Search by slip number or period'
    },
    wageLedgerEmpty: 'No wage ledger entries for this employee.',
    withholdingEmpty: 'No withholding slips for this employee.'
  },
  enums: {
    lineType: {
      accrual: 'Accrual',
      deduction: 'Deduction',
      earning: 'Earning',
      employerContribution: 'Employer Contribution',
      memo: 'Memo'
    },
    paymentAllocationRule: {
      fixedAmount: 'Fixed',
      remainder: 'Remainder'
    },
    paymentMethod: {
      bankTransfer: 'Bank transfer',
      cash: 'Cash'
    },
    recordSource: {
      engine: 'Engine',
      import: 'Import',
      manual: 'Manual'
    },
    runType: {
      bonus: 'Bonus',
      offCycle: 'Off-cycle',
      regular: 'Regular',
      retro: 'Retroactive',
      termination: 'Termination',
      yearEndAdj: 'Year-end Adjustment'
    },
    status: {
      approving: 'Approving',
      calculated: 'Calculated',
      calculating: 'Calculating',
      cancelled: 'Cancelled',
      delivered: 'Delivered',
      draft: 'Draft',
      finalized: 'Calculation completed',
      paid: 'Paid'
    }
  },
  members: {
    categoryTabs: {
      ariaLabel: 'Member category',
      board: 'Board',
      employee: 'Employees'
    },
    columns: {
      employmentType: 'Employment type',
      member: 'Member',
      organization: 'Organization',
      position: 'Position',
      status: 'Status',
      workLocation: 'Work location'
    },
    employmentType: {
      board_member: 'Board member',
      full_time: 'Full-time'
    },
    empty: 'No members found.',
    error: 'Failed to load members.',
    searchPlaceholder: 'Search by name, email, organization, or position',
    status: {
      active: 'Active',
      terminated: 'Terminated'
    }
  },
  overview: {
    alerts: {
      due: 'Due {{date}}',
      empty: 'No open alerts.',
      errorCount: 'Errors',
      helpTooltip: 'Open alerts across pay slips, wage ledgers, attendance and scheduled changes. Errors should be cleared before finalizing or paying.',
      title: 'Alerts needing action',
      viewAll: 'View all alerts',
      warningCount: 'Warnings'
    },
    breakdown: {
      department: {
        helpTooltip: 'Labor cost of the latest recorded month by department, with the change against the previous month.',
        title: 'Labor cost by department'
      },
      empty: 'Nothing was recorded in the latest month.',
      establishment: {
        helpTooltip: 'Labor cost of the latest recorded month by establishment (work location), with the change against the previous month.',
        title: 'Labor cost by establishment'
      },
      headcount_one: '{{count}} employee',
      headcount_other: '{{count}} employees'
    },
    empty: {
      subtitle: 'No pay was recorded in the last 12 months.',
      title: 'No payroll records yet'
    },
    error: 'Failed to load.',
    kpi: {
      comparison: 'MoM',
      grossAmount: 'Gross pay',
      grossAmountTooltip: 'Total earnings before deductions for the latest recorded month, including bonuses.',
      latestMonth: '{{month}}',
      latestMonthTooltip: 'Figures are for the most recent month that actually has pay recorded — not necessarily the current month. Pay is recorded on its payment date after the period closes, so the current month often has no finalized data yet.',
      netAmount: 'Net pay',
      netAmountTooltip: 'Gross pay minus deductions — the amount actually paid to employees.',
      noComparison: 'No prior month',
      noData: '–',
      totalLaborCost: 'Total labor cost',
      totalLaborCostTooltip: 'Gross pay plus employer contributions — what the company actually bears.'
    },
    trend: {
      helpTooltip: 'Monthly gross pay and employer contributions over the last 12 months, based on payment date. The two bars add up to total labor cost. Monthly average divides that total by the months that actually had records; deduction rate is deductions over gross pay, and employer share is employer contributions over total labor cost.',
      series: {
        employerContribution: 'Employer',
        grossAmount: 'Gross'
      },
      summary: {
        deductionRate: 'Deduction rate',
        employerContributionRate: 'Employer share',
        monthlyAverage: 'Monthly average',
        periodTotal: 'Labor cost (12 months)'
      },
      title: 'Labor cost trend'
    }
  },
  pageTitle: 'Salary & Wages',
  recordDetail: {
    /** Action menu in the detail panel header. Demo shows a toast instead of acting */
    actions: {
      deliver: 'Deliver',
      deliverToastDescription: 'Delivery is not available in the demo.',
      deliverToastTitle: 'Deliver',
      download: 'Download',
      downloadToastDescription: 'Download is not available in the demo.',
      downloadToastTitle: 'Download'
    },
    amountSummary: {
      accrualTotal: 'Accruals',
      employerContributionTotal: 'Employer Contribution',
      grossAmount: 'Gross Amount',
      mismatchWarning: 'Header totals disagree with line totals on: {{fields}}. Do not treat this record as reliable.',
      mismatchWarningBadge: 'Mismatch',
      netAmount: 'Net Amount',
      totalDeductions: 'Total Deductions',
      totalLaborCost: 'Total Labor Cost'
    },
    attendance: {
      amendedWarning: 'Attendance for this record was amended after the period was closed. Verify whether a retroactive difference needs to be issued.',
      amendedWarningBadge: 'Amended after close',
      days: {
        absenceDays: 'Absence days',
        daysWorked: 'Days worked',
        legalHolidayWorkdays: 'Legal holiday workdays',
        paidLeaveDays: 'Paid leave days',
        restDayWorkdays: 'Rest day workdays',
        scheduledWorkdays: 'Scheduled workdays',
        title: 'Days',
        totalWorkdays: 'Total workdays',
        unpaidLeaveDays: 'Unpaid leave days'
      },
      hours: {
        earlyLeaveHours: 'Early leave hours',
        hourlyBasisHours: 'Hourly rate basis hours',
        lateHours: 'Late hours',
        legalHolidayHours: 'Legal holiday hours',
        nightHours: 'Night hours',
        otOver60h: 'OT over 60h/month',
        restDayHours: 'Rest day hours',
        scheduledWorkHours: 'Scheduled work hours',
        statutoryOtOver: 'Statutory OT (over)',
        statutoryOtWithin: 'Statutory OT (within)',
        title: 'Hours',
        totalWorkHours: 'Total work hours'
      },
      traceability: {
        aeBatchRef: 'AE Batch Ref.',
        amendedFlag: 'Amended After Close',
        amendedFlagNo: 'No',
        amendedFlagYes: 'Yes',
        attendanceFinalizedAt: 'Attendance Finalized At',
        title: 'Upstream Traceability',
        workingTimeScheme: 'Working Time Scheme'
      },
      /** Unit suffix for the Value column; differs between the day and hour blocks */
      units: {
        day: 'days',
        hour: 'hours'
      },
      weeklyOtBreakdown: {
        columns: {
          otHours: 'OT Hours',
          weekStart: 'Week Start',
          workHours: 'Work Hours'
        },
        note: 'Under US FLSA, overtime is determined per calendar workweek, not per pay period. Even in a semi-monthly payroll, overtime is recalculated at each week boundary — this is why this breakdown only appears for US records.',
        title: 'Weekly Overtime Breakdown'
      }
    },
    error: 'Failed to load the salary record.',
    header: {
      calcLogicVersion: 'Calc Logic',
      calculationPeriod: 'Calculation Period',
      department: 'Department',
      employeeCode: 'Employee No.',
      employeeName: 'Name',
      finalizedAt: 'Finalized At',
      finalizedBy: 'Finalized By',
      issueDate: 'Issue Date',
      lockedBadge: 'Finalized · Locked',
      paymentDate: 'Payment Date',
      payrollRunId: 'Payroll Run',
      rateMasterVersion: 'Rate Master',
      recordNumber: 'Record No.',
      revisionNumber: 'Revision'
    },
    lineAbsenceNote: 'Negative earning shown here (absence adjustment)',
    /** Distribution of net pay across payees (E5) */
    paymentAllocation: {
      /** Reads as "Barclays Shoreditch Branch · ****7126" */
      accountSummary: '{{bank}} {{branch}} · {{account}}',
      mismatchWarning: 'Allocated total {{allocated}} does not match net pay {{netAmount}}. Verify that no payment was dropped.',
      mismatchWarningBadge: 'Allocation mismatch',
      /** Reads as "Bank transfer (Remainder)" */
      rowLabel: '{{method}} ({{rule}})'
    },
    sections: {
      attendance: 'Attendance',
      paymentAllocation: 'Transfer',
      postTax: 'Post-tax',
      preTax: 'Pre-tax',
      totals: 'Totals'
    },
    table: {
      amount: 'Amount',
      itemCode: 'Code',
      itemName: 'Pay slip item',
      /** Change vs the immediately preceding pay run (previous month for salary, previous run for bonus) */
      previousDelta: 'vs previous',
      quantity: 'Qty',
      rate: 'Rate',
      /** Header for a column mixing money, hours and days */
      value: 'Value'
    }
  },
  /**
   * Salary & wage records ledger. One row is the result a single payroll run finalized;
   * the per-employee breakdown lives in the record detail.
   */
  records: {
    columns: {
      finalizedDate: 'Finalized',
      grossAmount: 'Gross Amount',
      headcount: 'Headcount',
      netAmount: 'Net Amount',
      operation: 'Operation',
      recordName: 'Record',
      status: 'Status'
    },
    empty: 'No records match the current filters.',
    error: 'Failed to load records.',
    /**
     * Operation-kind filter tabs. The two kind labels are reused from
     * pages.payrollCore.configScreen.kinds so the ledger and payroll-core stay
     * on one vocabulary; only the "no filter" option lives here.
     */
    filterTabs: {
      all: 'All',
      ariaLabel: 'Filter by operation kind'
    },
    /** Record name: run type plus the target period, e.g. "Regular Salary – Jul 2026" */
    recordNameFormat: '{{runType}} – {{period}}',
    searchPlaceholder: 'Search by record, pay group or period…',
    unitEmployees: '{{count}}'
  },
  reports: {
    categories: {
      inHouse: 'In-house Reports',
      individual: 'Individual',
      organization: 'Organization'
    },
    columns: {
      collectedTax: 'Collected Tax',
      deductions: 'Deductions',
      dueDate: 'Due Date',
      employee: 'Employee',
      employerContribution: 'Employer Cost',
      gross: 'Gross',
      headcount: 'Headcount',
      issueDate: 'Issue Date',
      monthsCovered: 'Months',
      net: 'Net',
      nontaxableGross: 'Non-taxable',
      office: 'Office',
      paidAmount: 'Paid Amount',
      period: 'Period',
      recordNumber: 'Record No.',
      socialInsurance: 'Social Insurance',
      status: 'Status',
      targetMonth: 'Target Month',
      targetPeriod: 'Target Period',
      taxYear: 'Year',
      taxableGross: 'Taxable',
      totalLaborCost: 'Total Labor Cost',
      withheldTax: 'Withheld Tax'
    },
    empty: 'No data for this report.',
    error: 'Failed to load the report.',
    inHouseColumns: {
      avgFemale: 'Female Avg.',
      avgMale: 'Male Avg.',
      avgRaiseRate: 'Avg. Raise Rate',
      department: 'Department',
      gapRate: 'Gap Rate',
      headcount: 'Headcount',
      jobGrade: 'Job Grade',
      maxPay: 'Max',
      medianPay: 'Median',
      minPay: 'Min',
      promotionCount: 'Promotions',
      raiseCount: 'Raises',
      reviewDate: 'Review Date',
      status: 'Status',
      targetCount: 'Targets',
      updatedAt: 'Updated'
    },
    names: {
      bonusPayslip: 'Bonus Payslip',
      fairnessCheck: 'Fairness Check Report',
      incomeTaxStatement: 'Statement of Collected Income Tax',
      jobGradePayRange: 'Pay Range by Job Grade',
      laborInsuranceDeclaration: 'Labour Insurance Annual Declaration',
      payslip: 'Payslip',
      promotionSummary: 'Raise & Promotion Summary',
      residentTaxStatement: 'Resident Tax Payment Breakdown',
      retirementWithholdingSlip: 'Retirement Income Withholding Tax Certificate',
      salaryPaymentReport: 'Salary Payment Report (Summary)',
      standardRemunerationNotification: 'Standard Remuneration Calculation Notification',
      statutoryReportSummary: 'Summary of Statutory Reports',
      wageLedger: 'Wage Ledger',
      withholdingSlip: 'Withholding Tax Certificate'
    },
    searchPlaceholder: 'Search…',
    sidebar: {
      close: 'Close sidebar',
      open: 'Open sidebar',
      reportsTitle: 'Reports',
      title: 'Reports'
    },
    wageLedger: {
      bookColumns: {
        deductionTotal: 'Deductions Total',
        grossAmount: 'Gross Pay',
        netAmount: 'Net Pay',
        organization: 'Establishment',
        period: 'Covered Period',
        recordCount: 'Records',
        updatedAt: 'Last Updated',
        year: 'Year'
      },
      columns: {
        absenceDays: 'Absence Days',
        absenceDeduction: 'Absence Deduction',
        baseSalary: 'Base Salary',
        birthDate: 'Date of Birth',
        bonus: 'Bonus',
        childCareSupport: 'Child Care Support Levy',
        childCareSupportRetirementMonth: 'Child Care Support Levy (Retirement Month)',
        commuteAllowance: 'Commuting Allowance',
        daysWorked: 'Days Worked',
        deductionTotal: 'Deductions Total',
        deemedOtAllowance: 'Deemed Overtime Allowance',
        deemedOtExcessAllowance: 'Deemed Overtime Excess Allowance',
        employeeCode: 'Employee No.',
        employmentInsurance: 'Employment Insurance',
        executiveRemuneration: 'Executive Remuneration',
        expenseReimbursement: 'Expense Reimbursement',
        fixedOtAmount: 'Fixed Overtime Amount',
        fixedPeriodEnd: 'Period End (Fixed Pay)',
        fixedPeriodStart: 'Period Start (Fixed Pay)',
        gender: 'Gender',
        grossAmount: 'Gross Pay',
        healthInsurance: 'Health Insurance',
        healthInsuranceRetirementMonth: 'Health Insurance (Retirement Month)',
        healthInsuranceStandardRemuneration: 'Standard Monthly Remuneration (Health Ins.)',
        incomeTax: 'Income Tax',
        lateEarlyDeduction: 'Late / Early Leave Deduction',
        lateEarlyHours: 'Late / Early Leave',
        legalHolidayAllowance: 'Statutory Holiday Allowance',
        legalHolidayHours: 'Statutory Holiday Hours',
        longTermCare: 'Long-Term Care Insurance',
        longTermCareRetirementMonth: 'Long-Term Care Insurance (Retirement Month)',
        monthlyTaxCut: 'Monthly Fixed Tax Reduction',
        netAmount: 'Net Pay',
        nightAllowance: 'Night Allowance',
        nightHours: 'Night Hours',
        nontaxableCommute: 'Non-taxable Commuting Allowance',
        nontaxableCommuteFixed: 'Non-taxable Commuting (Fixed)',
        nontaxableCommuteVariable: 'Non-taxable Commuting (Variable)',
        nontaxableGrossTotal: 'Non-taxable Earnings Total',
        otOver60Allowance: 'Overtime Allowance (Over 60h)',
        otOver60Hours: 'Overtime Hours Over 60h',
        otWithinAllowance: 'OT Allowance (Within Statutory)',
        over45hAllowance: 'Allowance Over 45h',
        overtimeAllowance: 'Overtime Allowance',
        overtimeHours: 'Overtime Hours',
        payBasis: 'Pay Basis',
        paymentDate: 'Payment Date',
        pension: 'Employees’ Pension Insurance',
        pensionRetirementMonth: 'Employees’ Pension Insurance (Retirement Month)',
        pensionStandardRemuneration: 'Standard Monthly Remuneration (Pension)',
        premiumActualTotal: 'Premium Pay (Actual) Total',
        premiumTotal: 'Premium Pay Total',
        previousMonthAdjustment: 'Previous Month Adjustment',
        residentTax: 'Resident Tax',
        retroAllowance: 'Retroactive Allowance',
        runType: 'Run Type',
        scheduledWorkHours: 'Scheduled Hours',
        shortHours: 'Shortfall Hours',
        shortHoursDeduction: 'Shortfall Hours Deduction',
        socialInsuranceAdjustment: 'Social Insurance Adjustment Allowance',
        socialInsuranceTotal: 'Social Insurance Deductions Total',
        statutoryOtWithinHours: 'OT Hours Within Statutory',
        taxableBase: 'Taxable Base',
        taxableCommute: 'Taxable Commuting Allowance',
        taxableCommuteFixed: 'Taxable Commuting (Fixed)',
        taxableCommuteVariable: 'Taxable Commuting (Variable)',
        taxableGrossTotal: 'Taxable Earnings Total',
        totalWorkHours: 'Total Worked Hours',
        travelSettlement: 'Travel Expense Settlement',
        variablePeriodEnd: 'Period End (Variable Pay)',
        variablePeriodStart: 'Period Start (Variable Pay)',
        yearEndCollection: 'Year-End Adjustment Collected',
        yearEndRefund: 'Year-End Adjustment Refund',
        yearEndSettlement: 'Year-End Adjustment Settlement'
      },
      detail: {
        breakdown: 'Pay periods',
        error: 'Failed to load the wage ledger.',
        total: 'Ledger total'
      },
      /** 時間の列は「時間」「分」に分けて記載する。ラベルは共通の語幹から組む */
      hoursUnit: '{{label}} (h)',
      minutesUnit: '{{label}} (min)'
    }
  },
  ruleAndPolicy: {
    clause: {
      /** 業務委託・外部協力者のカードが編集不可な理由（tooltip） */
      disabledWorkerTypeReason: 'Payroll assignment rules only cover employees and board members. Contractors and collaborators are out of scope for payroll, so this setting is fixed.'
    },
    description: 'Decide which members each payroll structure applies to. Rules are evaluated in order and only the first match applies.',
    detail: {
      saved: 'Assignment rule saved.',
      unassignedNotice: 'This payroll structure has no assignment rule yet. Select the target organizations and member types to create one.'
    },
    groupName: 'Payroll structures',
    loadError: 'Failed to load assignment rules.',
    ruleName: 'Assignment rule',
    sideTab: {
      assignmentRules: 'Assignment Rules',
      closeSideTab: 'Close side tab',
      payrollStructures: 'Payroll structures'
    },
    table: {
      columns: {
        description: 'Description',
        memberCount: 'Worker Total',
        order: 'Order',
        structureName: 'Payroll structure'
      },
      searchPlaceholder: 'Search by payroll structure',
      unassigned: 'Not configured'
    }
  },
  settings: {
    establishments: {
      add: 'Add establishment',
      columns: {
        handle: 'Reorder',
        name: 'Establishment',
        orgUnits: 'Organizations',
        period: 'Effective period',
        places: 'Places'
      },
      description: 'An establishment is the unit that labor standards and occupational safety law apply to. Combine places and organizations so that every employee belongs to an establishment.\nEach employee is assigned to the first establishment that matches, evaluated from the top.',
      editor: {
        createTitle: 'Add establishment',
        discardConfirm: 'Discard your changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit: {{name}}',
        orgUnitTree: {
          addUnit: 'Add organization unit',
          allSubordinates: 'All subordinate units',
          collapse: 'Collapse',
          expand: 'Expand',
          removeUnit: 'Remove organization unit',
          removeUnitAriaLabel: 'Remove {{name}} from the tree'
        },
        saveCreate: 'Create establishment',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          orgUnits: 'Organization scope',
          places: 'Location scope'
        },
        tabs: {
          basic: 'Details',
          obligations: 'Statutory requirement',
          preview: 'Applicable employees'
        },
        unassignConfirmCancel: 'Cancel',
        unassignConfirmMessage_one: 'This change leaves {{count}} employee unassigned.',
        unassignConfirmMessage_other: 'This change leaves {{count}} employees unassigned.',
        unassignConfirmOk: 'Save anyway',
        unassignConfirmTitle: 'Confirm the change'
      },
      empty: {
        addManually: 'Add manually',
        createFromPlaces: 'Generate candidates',
        description: 'No establishments yet. You can generate candidates from your places.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load establishments.'
      },
      filters: {
        searchPlaceholder: 'Search by name or code'
      },
      fields: {
        includeDescendants: {
          help: 'Employees in descendant organizations are also included.',
          label: 'Include descendant organizations'
        },
        industryClass: {
          label: 'Industry class'
        },
        laborStandardsOffice: {
          label: 'Labor standards inspection office',
          placeholder: 'Select an office'
        },
        locationPlace: {
          label: 'Location',
          required: 'Select a location.'
        },
        name: {
          label: 'Name',
          required: 'Enter a name.',
          tooLong: 'Use 64 characters or fewer.'
        },
        orgUnits: {
          label: 'Organizations',
          placeholder: 'Select organizations'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          placeholder: 'Select an end date'
        },
        periodStart: {
          label: 'Start date',
          required: 'Enter a start date.'
        },
        places: {
          clearAll: 'Clear all places',
          headcountSuffix: '{{count}} on site',
          label: 'Places',
          mergedHint: 'More than one place is referenced. This is a merge exception, so a scope rationale is required.',
          noResults: 'No results found',
          searchPlaceholder: 'Search places...',
          usedBy: 'Used by {{name}}'
        },
        removeSelection: 'Remove {{name}}',
        scopeRationale: {
          help: 'Explain the merge or split so it can be shown to the inspection office.',
          label: 'Scope rationale',
          placeholder: 'e.g. The Nagoya office has 4 permanent staff and no HR or payroll authority, so it is merged into the head office.',
          requiredOnMerge: 'A rationale is required when more than one place is referenced.'
        },
        selectedCount: '{{count}} selected',
        sourceEmpty: 'Select at least one place or organization.'
      },
      industryClass: {
        groupA: 'Group A (construction, mining, transport)',
        groupB: 'Group B (manufacturing, utilities, retail)',
        groupC: 'Group C (other industries)'
      },
      obligations: {
        appointments: '{{count}} or more required',
        headcount: 'Regular employees (as of the reference date)',
        minimumWage: 'Applicable regional minimum wage',
        nearThreshold: '{{count}} more employees until this applies',
        notApplicableIndustry: 'Not applicable to {{industry}}',
        notRequired: 'Not required',
        required: 'Required',
        threshold: '{{count}} or more',
        titles: {
          exclusivePhysician: 'Full-time industrial physician',
          generalSafetyManager: 'General safety and health manager',
          healthCommittee: 'Health committee',
          healthOfficer: 'Health officer',
          industrialPhysician: 'Industrial physician',
          safetyOfficer: 'Safety officer',
          stressCheck: 'Stress check',
          workRules: 'Work rules filing'
        }
      },
      override: {
        establishment: 'Establishment',
        note: 'Note',
        reason: 'Reason',
        reasons: {
          concurrent: 'Concurrent post',
          other: 'Other',
          secondment: 'Secondment',
          telework: 'Telework'
        },
        removedToast: 'Assignment added',
        title: 'Assign individually',
        undo: 'Undo'
      },
      period: {
        ended: 'Ended',
        future: 'Starts in the future',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        calculating: 'Calculating…',
        columns: {
          name: 'Name',
          orgUnit: 'Organization',
          place: 'Place',
          status: 'Status'
        },
        noDiff: 'No change in assignment',
        noMembers: 'No employees match this definition.',
        noSource: 'Select a source to see the preview.',
        reviewSource: 'Review the source',
        statusAdded: 'Will be added on save',
        statusNotApplicable: 'Covered by a higher-priority establishment',
        statusRemoved: 'Will be removed on save',
        summary: {
          diff: 'Change since the last save',
          diffValue: '+{{added}} / −{{removed}}',
          headcount: 'Applicable employees',
          notApplicable: 'Covered by a higher-priority establishment'
        }
      },
      reorder: {
        announce: 'Moved {{name}} to position {{position}}',
        cancel: 'Cancel',
        disabledHint: 'Clear the filters to change the order. Reordering needs the full list.',
        grabAriaLabel: 'Reorder {{name}}',
        grabbed: 'Grabbed {{name}}. Use the arrow keys to move it.',
        hint: 'Evaluated from the top. The first match wins.',
        save: 'Save order',
        savedToast: 'Order saved'
      },
      source: {
        more: '+{{count}}',
        none: '–',
        withDescendants: 'incl. descendants'
      },
      title: 'Establishments',
      unassigned: {
        actions: {
          createEstablishment: 'Create an establishment for this place',
          mergeIntoExisting: 'Merge into an existing establishment',
          overrideIndividually: 'Assign individually',
          snooze: 'Handle later'
        },
        backToRegistry: 'Back to establishments',
        causeHelp: {
          establishmentOutOfPeriod: 'The establishment that used to cover this place has ended. Extend its period or move the place to another establishment.',
          noWorkPlace: 'A home address is not an establishment. Choose the establishment these employees belong to. The applicable minimum wage follows that establishment’s location.',
          placeNotReferenced: 'Merge this place into an existing establishment, or create a new establishment for it.',
          secondment: 'Assign these employees individually so their working hours are managed by the right establishment.'
        },
        causes: {
          establishmentOutOfPeriod: 'The referencing establishment is outside its effective period',
          noWorkPlace: 'No work location set',
          placeNotReferenced: 'The work location does not belong to any establishment',
          secondment: 'On secondment'
        },
        emptyTitle: 'Every employee is assigned to an establishment.',
        selectEmployee: 'Select an employee to resolve.',
        title: 'Resolve assignments'
      },
      validation: {
        missingAttribute: 'Attributes missing',
        noMember: 'No members',
        ok: 'Normal',
        outOfPeriod: 'Out of period',
        shadowed: 'Higher-priority establishment applies',
        shadowedDetail_one: '{{count}} employee is covered by a higher-priority establishment',
        shadowedDetail_other: '{{count}} employees are covered by a higher-priority establishment',
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    inHouseReports: {
      add: 'Add in-house report',
      columns: {
        groupCount: 'Groups',
        name: 'Report name',
        period: 'Effective period'
      },
      description: 'An in-house report is a tenant-defined document that does not follow a statutory form.\nDefine groups and items freely, and link each item to a payroll item.',
      editor: {
        addItem: 'Add item',
        createTitle: 'Add in-house report',
        discardConfirm: 'Discard your changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptySection: 'This group has no items.',
        /** Group controls. The heading itself is data because the form is tenant-defined. */
        groups: {
          add: 'Add group',
          defaultLabel: 'New group',
          labelAriaLabel: 'Group heading',
          labelPlaceholder: 'Group heading printed on the report',
          moveAnnounce: 'Moved {{name}} to position {{position}}',
          moveDownAriaLabel: 'Move {{name}} down',
          moveUpAriaLabel: 'Move {{name}} up',
          removeAriaLabel: 'Remove {{name}}'
        },
        itemColumns: {
          handle: 'Order',
          label: 'Report item name',
          payrollItem: 'Payroll item',
          remove: 'Remove'
        },
        labelPlaceholder: 'Item name printed on the report',
        payrollItem: {
          noResults: 'No matching payroll item',
          none: 'Not linked',
          placeholder: 'Select a payroll item',
          searchPlaceholder: 'Search payroll items...'
        },
        removeItemAriaLabel: 'Remove {{name}}',
        reorder: {
          announce: 'Moved {{name}} to position {{position}}',
          grabAriaLabel: 'Reorder {{name}}',
          grabbed: 'Grabbed {{name}}. Use the arrow keys to move it.',
          hint: 'Items print in this order. Reordering is limited to within a group.'
        },
        saveCreate: 'Create in-house report',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          header: 'Header'
        },
        tabs: {
          detail: 'Details',
          items: 'Report items',
          preview: 'Document preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No in-house reports yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load in-house reports.'
      },
      fields: {
        name: {
          label: 'Name',
          required: 'Enter a name.',
          tooLong: 'Use 64 characters or fewer.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'The end date must be on or after the start date.',
          placeholder: 'Select an end date'
        },
        periodStart: {
          required: 'Enter a start date.'
        }
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      groups: {
        count_one: '{{count}} group',
        count_other: '{{count}} groups'
      },
      /** Fixed header fields. Also used as the default item names on creation. */
      headerFields: {
        calculationPeriod: 'Target period',
        department: 'Department',
        employeeCode: 'Employee code',
        employeeName: 'Name',
        establishment: 'Establishment',
        paymentDate: 'Payment date'
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired up in this mock yet.',
        downloadToastTitle: 'Download requested',
        groupTotal: 'Subtotal',
        issuer: 'DRESS CODE, Inc.',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        samplePlaceholder: 'Sample',
        sampleValues: {
          department: 'People Ops',
          employeeCode: 'E-1042',
          employeeName: 'Taro Yamada',
          establishment: 'Head office (Minato)'
        },
        seal: 'Seal',
        untitled: '(Untitled)'
      },
      title: 'In-house reports',
      validation: {
        showOutOfPeriod: 'Show reports outside their effective period'
      }
    },
    navigation: {
      bonusPaySlips: 'Bonus pay slips',
      establishments: 'Establishments',
      inHouseReports: 'In-house reports',
      incomeTaxStatements: 'Income tax statements',
      label: 'Settings menu',
      laborInsuranceDeclarations: 'Labor insurance declarations',
      paySlips: 'Pay slips',
      payrollReferenceCatalog: 'Payroll reference catalog',
      residentTaxStatements: 'Resident tax statements',
      retirementWithholdingSlips: 'Retirement withholding slips',
      salaryPaymentReports: 'Salary payment reports',
      sections: {
        individual: 'Individual reports',
        organization: 'Organization reports'
      },
      standardRemunerationNotifications: 'Standard remuneration notifications',
      statutoryReportSummaries: 'Statutory report summaries',
      wageLedgers: 'Wage ledgers',
      withholdingSlips: 'Withholding slips'
    },
    paySlips: {
      add: 'Add pay slip',
      columns: {
        name: 'Pay slip name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'A pay slip definition decides which items are printed on the statement and under which names.\nLink it to the payroll structures that should use it.',
      editor: {
        addItem: 'Add item',
        createTitle: 'Add pay slip',
        discardConfirm: 'Discard your changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit: {{name}}',
        emptySection: 'No items in this category.',
        itemColumns: {
          handle: 'Reorder',
          label: 'Pay slip item name',
          payrollItem: 'Payroll item',
          remove: 'Remove'
        },
        labelPlaceholder: 'Name printed on the statement',
        payrollItem: {
          noResults: 'No payroll items found',
          none: 'Not linked',
          placeholder: 'Select a payroll item',
          searchPlaceholder: 'Search payroll items...'
        },
        removeItemAriaLabel: 'Remove {{name}}',
        reorder: {
          announce: 'Moved {{name}} to position {{position}}',
          grabAriaLabel: 'Reorder {{name}}',
          grabbed: 'Grabbed {{name}}. Use the arrow keys to move it.',
          hint: 'Items are printed top to bottom. Reordering stays inside a category.'
        },
        saveCreate: 'Create pay slip',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          header: 'Header'
        },
        tabs: {
          detail: 'Details',
          items: 'Items',
          preview: 'Document style preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No pay slips yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load pay slips.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      /** 宛先ブロックの固定フィールド名。新規作成時の項目名の初期値にも使う */
      headerFields: {
        calculationPeriod: 'Pay period',
        department: 'Department',
        employeeCode: 'Employee number',
        employeeName: 'Name',
        establishment: 'Establishment',
        paymentDate: 'Payment date'
      },
      fields: {
        name: {
          label: 'Name',
          required: 'Enter a name.',
          tooLong: 'Use 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'The end date must be on or after the start date.',
          placeholder: 'Select an end date'
        },
        periodStart: {
          required: 'Enter a start date.'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Starts in the future',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        documentTitle: 'Pay Statement',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired up in this mock yet.',
        downloadToastTitle: 'Download requested',
        issuer: 'DRESS CODE, Inc.',
        sampleNotice: 'Amounts and dates are sample values for checking the layout.',
        samplePlaceholder: 'Sample',
        sampleValues: {
          department: 'People Department',
          employeeCode: 'E-1042',
          employeeName: 'Taro Yamada',
          establishment: 'Head office (Minato)'
        },
        seal: 'Seal',
        /** 勤怠ブロックの値に付ける単位 */
        units: {
          day: 'days',
          hour: 'hours'
        }
      },
      sections: {
        attendance: 'Attendance',
        deduction: 'Deductions',
        earning: 'Earnings',
        total: 'Totals'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Pay slips',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    payrollReferenceCatalog: {
      add: 'Add payroll reference',
      columns: {
        description: 'Description',
        name: 'Name',
        period: 'Effective period'
      },
      /** 読み先。コア DB 3 つと、顧客の規程が定める社内マスタ */
      coreSources: {
        attendances: 'Attendance',
        contracts: 'Contracts',
        'in-house': 'In-house master',
        people: 'Members'
      },
      description: 'The payroll reference catalog defines where payroll reads the information it already holds. Core database extracts map to a single database each and hold only the information items payroll needs, worker by worker; in-house masters are the lookup tables your own wage and expense rules define.\nEither way the values are settled in house and frozen as of the payroll base date. Tables set by law live in statutory standards instead.',
      editor: {
        createTitle: 'Add payroll reference',
        discardConfirm: 'Discard your changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit: {{name}}',
        saveCreate: 'Create payroll reference',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information'
        },
        tabs: {
          detail: 'Details',
          table: 'Table'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No payroll references yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load payroll references.'
      },
      fields: {
        coreSource: {
          label: 'Read from'
        },
        description: {
          label: 'Description',
          placeholder: 'Describe what can be referenced, which core database it is frozen from and what the caveats are',
          required: 'Enter a description.',
          tooLong: 'Use 1000 characters or fewer.'
        },
        name: {
          label: 'Name',
          required: 'Enter a name.',
          tooLong: 'Use 64 characters or fewer.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'The end date must be on or after the start date.',
          placeholder: 'Select an end date'
        },
        periodStart: {
          required: 'Enter a start date.'
        }
      },
      filters: {
        searchPlaceholder: 'Filter by name'
      },
      /** 対応する payroll item。1 対 1 の対応はカタログ側が決めるため読み取り専用 */
      payrollItem: {
        label: 'Linked payroll item',
        none: 'Not linked to a payroll item yet.',
        note: 'One reference target maps to exactly one payroll item. That pairing is owned by the payroll item catalog, so it cannot be changed here.',
        value: '{{code}} {{name}}'
      },
      period: {
        ended: 'Ended',
        future: 'Starts in the future',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      table: {
        empty: {
          description: 'A reference table is the result of extracting from a core database, so it is not authored here. Payroll references you add on this screen start with no table.',
          title: 'No reference table.'
        }
      },
      title: 'Payroll reference catalog',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    bonusPaySlips: {
      add: 'Add bonus pay slip',
      columns: {
        name: 'Bonus pay slip name',
        period: 'Effective period',
        structures: 'Bonus payroll structures'
      },
      description: 'A bonus pay slip definition decides which items are printed on the statement and under which names.\nLink it to the bonus payroll structures that should use it.',
      editor: {
        addItem: 'Add item',
        createTitle: 'Add bonus pay slip',
        discardConfirm: 'Discard your changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit: {{name}}',
        emptySection: 'No items in this category.',
        itemColumns: {
          handle: 'Reorder',
          label: 'Bonus pay slip item name',
          payrollItem: 'Payroll item',
          remove: 'Remove'
        },
        labelPlaceholder: 'Name printed on the statement',
        payrollItem: {
          noResults: 'No payroll items found',
          none: 'Not linked',
          placeholder: 'Select a payroll item',
          searchPlaceholder: 'Search payroll items...'
        },
        removeItemAriaLabel: 'Remove {{name}}',
        reorder: {
          announce: 'Moved {{name}} to position {{position}}',
          grabAriaLabel: 'Reorder {{name}}',
          grabbed: 'Grabbed {{name}}. Use the arrow keys to move it.',
          hint: 'Items are printed top to bottom. Reordering stays inside a category.'
        },
        saveCreate: 'Create bonus pay slip',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          header: 'Header'
        },
        tabs: {
          detail: 'Details',
          items: 'Items',
          preview: 'Document style preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No bonus pay slips yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load bonus pay slips.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      headerFields: {
        bonusPaymentDate: 'Bonus payment date',
        department: 'Department',
        employeeCode: 'Employee number',
        employeeName: 'Name',
        establishment: 'Establishment'
      },
      fields: {
        name: {
          label: 'Name',
          required: 'Enter a name.',
          tooLong: 'Use 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Bonus payroll structures',
          placeholder: 'Select bonus payroll structures',
          required: 'Select at least one bonus payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'The end date must be on or after the start date.',
          placeholder: 'Select an end date'
        },
        periodStart: {
          required: 'Enter a start date.'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Starts in the future',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        documentTitle: 'Bonus Pay Statement',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired up in this mock yet.',
        downloadToastTitle: 'Download requested',
        issuer: 'DRESS CODE, Inc.',
        sampleNotice: 'Amounts and dates are sample values for checking the layout.',
        samplePlaceholder: 'Sample',
        sampleValues: {
          department: 'People Department',
          employeeCode: 'E-1042',
          employeeName: 'Taro Yamada',
          establishment: 'Head office (Minato)'
        },
        seal: 'Seal',
        units: {
          day: 'days',
          hour: 'hours'
        }
      },
      sections: {
        attendance: 'Attendance',
        deduction: 'Deductions',
        earning: 'Earnings',
        total: 'Totals'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Bonus pay slips',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    wageLedgers: {
      add: 'Add wage ledger',
      columns: {
        exportProfile: 'Export profile',
        name: 'Wage ledger name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'A wage ledger definition decides which items are printed in the ledger and under which names.\nMap statutory items under Labor Standards Rule 54 to payroll items.',
      editor: {
        addItem: 'Add item',
        createTitle: 'Add wage ledger',
        defaultItems: {
          baseSalary: 'Base salary',
          healthInsurance: 'Health insurance'
        },
        discardConfirm: 'Discard your changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptySection: 'No items in this category.',
        itemColumns: {
          handle: 'Order',
          label: 'Wage ledger item name',
          payrollItem: 'Payroll item',
          remove: 'Remove'
        },
        labelPlaceholder: 'Label printed on the ledger',
        payrollItem: {
          noResults: 'No matching payroll items',
          none: 'Not linked',
          placeholder: 'Select payroll item',
          searchPlaceholder: 'Search payroll items...'
        },
        removeItemAriaLabel: 'Remove {{name}}',
        reorder: {
          announce: 'Moved {{name}} to position {{position}}',
          grabAriaLabel: 'Reorder {{name}}',
          grabbed: 'Grabbed {{name}}. Use arrow keys to move.',
          hint: 'Items print top to bottom. Reordering works within each category only.'
        },
        saveCreate: 'Create wage ledger',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          header: 'Header'
        },
        tabs: {
          detail: 'Details',
          items: 'Line items',
          preview: 'Document preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No wage ledgers yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load wage ledgers.'
      },
      exportProfiles: {
        form20: 'Form 20',
        standard: 'Standard'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      headerFields: {
        employeeCode: 'Employee ID',
        employeeName: 'Employee name',
        establishment: 'Establishment',
        gender: 'Gender',
        wageCalculationPeriod: 'Wage calculation period'
      },
      fields: {
        exportProfile: {
          label: 'Export profile'
        },
        name: {
          label: 'Name',
          required: 'Enter a name.',
          tooLong: 'Use 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after the start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Enter a start date.'
        },
        statutory: {
          deductionItem: 'At least one deduction item',
          earningItem: 'At least one earning item',
          employeeName: 'Employee name (header)',
          gender: 'Gender (header)',
          piAttendanceHours: 'Attendance hours',
          piLateNightWorkHours: 'Late-night work hours',
          piOvertimeHours: 'Overtime hours',
          piStatutoryHolidayWorkHours: 'Statutory holiday work hours',
          piWorkDays: 'Work days',
          summary: 'Statutory items under Labor Standards Rule 54 are missing.',
          wageCalculationPeriod: 'Wage calculation period (header)'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        documentTitle: 'Wage Ledger',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download request received',
        issuer: 'DRESS CODE Inc.',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        samplePlaceholder: 'Sample',
        sampleValues: {
          employeeCode: 'E-1042',
          employeeName: 'Taro Yamada',
          establishment: 'Head office (Minato)',
          gender: 'Male'
        },
        statutoryColumns: {
          item: 'Item'
        },
        units: {
          day: 'days',
          hour: 'hours'
        }
      },
      sections: {
        attendance: 'Attendance',
        deduction: 'Deductions',
        earning: 'Earnings',
        total: 'Totals'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Wage ledgers',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    withholdingSlips: {
      add: 'Add withholding slip profile',
      columns: {
        name: 'Profile name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'Withholding slip profiles map statutory slip columns (③–㉘) to payroll items and year-end adjustment data.\nLink the payroll structures that use this profile.',
      editor: {
        createTitle: 'Add withholding slip profile',
        discardConfirm: 'Discard changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        mappingColumns: {
          column: 'Statutory column',
          label: 'Label on slip',
          sourceRef: 'Reference',
          sourceType: 'Data source'
        },
        saveCreate: 'Create profile',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          payer: 'Payer',
          remarks: 'Remarks template'
        },
        tabs: {
          detail: 'Detail',
          fieldMappings: 'Field mappings',
          preview: 'Slip preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No withholding slip profiles yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load withholding slip profiles.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      fields: {
        columns: {
          basicDeduction: 'Basic deduction (⑱)',
          dependents: 'Spouse and dependents (⑦)',
          paymentAmount: 'Payment amount (③)',
          remarks: 'Remarks (㉘)',
          salaryIncomeDeduction: 'Salary income after deduction (④)',
          socialInsurance: 'Social insurance premiums (⑬)',
          spouseDeduction: 'Spouse deduction (⑧)',
          totalIncomeDeduction: 'Total income deductions (⑤)',
          withholdingTax: 'Withholding tax (⑥)'
        },
        employeeMasterFields: {
          address: 'Address',
          dependents: 'Dependents',
          spouse: 'Spouse'
        },
        myNumberPolicy: {
          label: 'My Number policy',
          options: {
            maskEmployeeCopy: 'Mask on employee copy',
            taxOfficeCopyOnly: 'Tax office copy only'
          }
        },
        name: {
          label: 'Name',
          required: 'Enter a name.',
          tooLong: 'Use 64 characters or fewer.'
        },
        payer: {
          recipientNumberPrefix: {
            label: 'Recipient number prefix',
            placeholder: 'e.g. DC-'
          },
          taxOfficeCode: {
            label: 'Tax office code',
            placeholder: 'e.g. 01101'
          }
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after the start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Enter a start date.'
        },
        remarksTemplates: {
          label: 'Remarks template',
          placeholder: 'e.g. Salary and bonus for {{year}}'
        },
        sourceRef: {
          notApplicable: '—',
          placeholder: 'Select reference'
        },
        sourceType: {
          options: {
            computed: 'Computed',
            employeeMaster: 'Employee master',
            payrollItemAggregate: 'Payroll item aggregate',
            yearEndAdjustment: 'Year-end adjustment'
          }
        },
        statutory: {
          summary: 'Required columns under Income Tax Act Article 226 are missing or have no reference.'
        },
        yearEndAdjustmentFields: {
          basicDeduction: 'Basic deduction',
          earthquakeInsuranceDeduction: 'Earthquake insurance deduction',
          lifeInsuranceDeduction: 'Life insurance deduction',
          spouseDeduction: 'Spouse deduction',
          totalIncomeDeduction: 'Total income deductions'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        columns: {
          amount: 'Amount',
          item: 'Item'
        },
        documentTitle: 'Withholding Tax Certificate',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download request received',
        payerLabel: 'Payer',
        recipientLabel: 'Recipient',
        recipientNumberLabel: 'Recipient number',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        taxOfficeCodeLabel: 'Tax office code',
        taxYearLabel: 'Tax year {{year}}'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Withholding slips',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    retirementWithholdingSlips: {
      add: 'Add retirement withholding slips',
      columns: {
        name: 'Profile name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'Maps statutory columns (R8 form items 1–7) on retirement income withholding slips to payroll items and termination data.',
      editor: {
        createTitle: 'Add retirement withholding slips',
        discardConfirm: 'Discard changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptyWageItems: 'No wage items selected.',
        mappingColumns: {
          classification: 'Classification',
          column: 'Statutory column',
          field: 'Aggregate field',
          itemCount: 'Included items',
          label: 'Form label',
          payrollItem: 'Payroll item',
          sourceRef: 'Source reference',
          sourceType: 'Data source'
        },
        saveCreate: 'Create profile',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          payer: 'Payer',
          remarks: 'Remarks',
          wageItems: 'Total wage items'
        },
        tabs: {
          detail: 'Detail',
          fieldMappings: 'Column mapping',
          preview: 'Document preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No retirement withholding slips yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load retirement withholding slips.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      fields: {
        columns: {
          paymentAmount: '支払金額（1）',
          remarks: '摘要（7）',
          retirementAllowance: '退職手当等（5）',
          retirementIncomeDeduction: '退職所得控除（2）',
          taxableRetirementIncome: '課税退職所得（3）',
          withholdingTax: '源泉徴収税額（4）',
          yearsOfService: '勤続年数（6）'
        },
        employeeMasterFields: {
          address: 'Address',
          dependents: 'Dependents',
          spouse: 'Spouse'
        },
        myNumberPolicy: {
          label: 'My Number display policy',
          options: {
            maskEmployeeCopy: 'Mask employee copy',
            taxOfficeCopyOnly: 'Tax office copy only'
          }
        },
        name: {
          label: 'Name',
          required: 'Name is required.',
          tooLong: 'Must be 64 characters or fewer.'
        },
        payer: {
          recipientNumberPrefix: {
            label: 'Recipient number prefix',
            placeholder: 'e.g. DC-'
          },
          taxOfficeCode: {
            label: 'Tax office code',
            placeholder: 'e.g. 01101'
          }
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Start date is required.'
        },
        remarksTemplates: {
          label: 'Remarks template',
          placeholder: 'e.g. {{year}} retirement allowance'
        },
        sourceRef: {
          notApplicable: '—',
          placeholder: 'Select source'
        },
        sourceType: {
          options: {
            computed: 'Computed',
            employeeMaster: 'Employee master',
            payrollItemAggregate: 'Payroll item aggregate',
            yearEndAdjustment: 'Year-end adjustment'
          }
        },
        statutory: {
          summary: 'Required statutory fields are missing or unmapped.'
        },
        yearEndAdjustmentFields: {
          basicDeduction: 'Basic deduction',
          earthquakeInsuranceDeduction: 'Earthquake insurance deduction',
          lifeInsuranceDeduction: 'Life insurance deduction',
          spouseDeduction: 'Spouse deduction',
          totalIncomeDeduction: 'Total income deduction'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        columns: {
          amount: 'Amount',
          item: 'Item'
        },
        documentTitle: 'Retirement Income Withholding Slip',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download queued',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        totalWagesLabel: 'Total'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Retirement withholding slips',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    incomeTaxStatements: {
      add: 'Add income tax statements',
      columns: {
        name: 'Profile name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'Monthly withholding ledger column profile mapping payroll items to collected tax columns per office and month.',
      editor: {
        createTitle: 'Add income tax statements',
        discardConfirm: 'Discard changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptyWageItems: 'No wage items selected.',
        mappingColumns: {
          classification: 'Classification',
          column: 'Statutory column',
          field: 'Aggregate field',
          itemCount: 'Included items',
          label: 'Form label',
          payrollItem: 'Payroll item',
          sourceType: 'Data source'
        },
        saveCreate: 'Create profile',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          wageItems: 'Total wage items'
        },
        tabs: {
          columnMappings: 'Column profile',
          detail: 'Detail',
          preview: 'Document preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No income tax statements yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load income tax statements.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      fields: {
        columns: {
          employeeCount: '人員',
          paymentAmount: '支払金額',
          refundAmount: '還付税額',
          withholdingTax: '源泉徴収税額'
        },
        name: {
          label: 'Name',
          required: 'Name is required.',
          tooLong: 'Must be 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Start date is required.'
        },
        sourceRef: {
          placeholder: 'Select source'
        },
        statutory: {
          summary: 'Required statutory fields are missing or unmapped.'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        columns: {
          amount: 'Amount',
          item: 'Item'
        },
        documentTitle: 'Withholding Tax Ledger (Monthly)',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download queued',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        totalWagesLabel: 'Total'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Income tax statements',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    residentTaxStatements: {
      add: 'Add resident tax statements',
      columns: {
        name: 'Profile name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'Resident tax collection column profile mapping payroll items to special and ordinary collection amounts.',
      editor: {
        createTitle: 'Add resident tax statements',
        discardConfirm: 'Discard changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptyWageItems: 'No wage items selected.',
        mappingColumns: {
          classification: 'Classification',
          column: 'Statutory column',
          field: 'Aggregate field',
          itemCount: 'Included items',
          label: 'Form label',
          payrollItem: 'Payroll item',
          sourceType: 'Data source'
        },
        saveCreate: 'Create profile',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          wageItems: 'Total wage items'
        },
        tabs: {
          columnMappings: 'Column profile',
          detail: 'Detail',
          preview: 'Document preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No resident tax statements yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load resident tax statements.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      fields: {
        columns: {
          collectedTax: '徴収税額',
          employeeCount: '人員',
          ordinaryCollection: '普通徴収',
          specialCollection: '特別徴収'
        },
        name: {
          label: 'Name',
          required: 'Name is required.',
          tooLong: 'Must be 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Start date is required.'
        },
        sourceRef: {
          placeholder: 'Select source'
        },
        statutory: {
          summary: 'Required statutory fields are missing or unmapped.'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        columns: {
          amount: 'Amount',
          item: 'Item'
        },
        documentTitle: 'Resident Tax Payment Breakdown',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download queued',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        totalWagesLabel: 'Total'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Resident tax statements',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    statutoryReportSummaries: {
      add: 'Add statutory report summaries',
      columns: {
        name: 'Profile name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'Aggregate profile for statutory report summary totals per office and tax year.',
      editor: {
        createTitle: 'Add statutory report summaries',
        discardConfirm: 'Discard changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptyWageItems: 'No wage items selected.',
        mappingColumns: {
          classification: 'Classification',
          column: 'Statutory column',
          field: 'Aggregate field',
          itemCount: 'Included items',
          label: 'Form label',
          payrollItem: 'Payroll item',
          sourceRef: 'Source reference',
          sourceType: 'Data source'
        },
        saveCreate: 'Create profile',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          wageItems: 'Total wage items'
        },
        tabs: {
          aggregateMappings: 'Aggregate fields',
          detail: 'Detail',
          preview: 'Document preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No statutory report summaries yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load statutory report summaries.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      fields: {
        columns: {
          headcount: '人員',
          paidAmount: '支払金額',
          withheldTax: '源泉徴収税額'
        },
        name: {
          label: 'Name',
          required: 'Name is required.',
          tooLong: 'Must be 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Start date is required.'
        },
        removeSelection: 'Remove {{name}}',
        selectedCount: '{{count}} selected',
        sourceRef: {
          placeholder: 'Select source'
        },
        statutory: {
          summary: 'Required statutory fields are missing or unmapped.'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        columns: {
          amount: 'Amount',
          item: 'Item'
        },
        documentTitle: 'Summary of Statutory Reports',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download queued',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        totalWagesLabel: 'Total'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Statutory report summaries',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    salaryPaymentReports: {
      add: 'Add salary payment reports',
      columns: {
        name: 'Profile name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'Salary payment report field mapping with municipality filing fields such as special collection designation number.',
      editor: {
        createTitle: 'Add salary payment reports',
        discardConfirm: 'Discard changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptyWageItems: 'No wage items selected.',
        mappingColumns: {
          classification: 'Classification',
          column: 'Statutory column',
          field: 'Aggregate field',
          itemCount: 'Included items',
          label: 'Form label',
          payrollItem: 'Payroll item',
          sourceRef: 'Source reference',
          sourceType: 'Data source'
        },
        saveCreate: 'Create profile',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          municipality: 'Municipality filing fields',
          wageItems: 'Total wage items'
        },
        tabs: {
          detail: 'Detail',
          fieldMappings: 'Column mapping',
          preview: 'Document preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No salary payment reports yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load salary payment reports.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      fields: {
        columns: {
          headcount: '人員',
          paidAmount: '支払金額',
          remarks: '摘要',
          withholdingTax: '源泉徴収税額'
        },
        employeeMasterFields: {
          address: 'Address',
          dependents: 'Dependents',
          spouse: 'Spouse'
        },
        municipality: {
          ordinaryCollectionReasonCode: {
            label: 'Ordinary collection reason code',
            placeholder: 'e.g. 01'
          },
          specialCollectionDesignationNumber: {
            label: 'Special collection designation number',
            placeholder: 'e.g. 131016'
          }
        },
        name: {
          label: 'Name',
          required: 'Name is required.',
          tooLong: 'Must be 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Start date is required.'
        },
        sourceRef: {
          notApplicable: '—',
          placeholder: 'Select source'
        },
        sourceType: {
          options: {
            computed: 'Computed',
            employeeMaster: 'Employee master',
            payrollItemAggregate: 'Payroll item aggregate',
            yearEndAdjustment: 'Year-end adjustment'
          }
        },
        statutory: {
          summary: 'Required statutory fields are missing or unmapped.'
        },
        yearEndAdjustmentFields: {
          basicDeduction: 'Basic deduction',
          earthquakeInsuranceDeduction: 'Earthquake insurance deduction',
          lifeInsuranceDeduction: 'Life insurance deduction',
          spouseDeduction: 'Spouse deduction',
          totalIncomeDeduction: 'Total income deduction'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        columns: {
          amount: 'Amount',
          item: 'Item'
        },
        documentTitle: 'Salary Payment Report',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download queued',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        totalWagesLabel: 'Total'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Salary payment reports',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    laborInsuranceDeclarations: {
      add: 'Add labor insurance declarations',
      columns: {
        name: 'Profile name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'Defines which payroll items count toward total wages for labour insurance annual declaration.',
      editor: {
        createTitle: 'Add labor insurance declarations',
        discardConfirm: 'Discard changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptyWageItems: 'No wage items selected.',
        mappingColumns: {
          classification: 'Classification',
          column: 'Statutory column',
          field: 'Aggregate field',
          itemCount: 'Included items',
          label: 'Form label',
          payrollItem: 'Payroll item',
          sourceType: 'Data source'
        },
        saveCreate: 'Create profile',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          wageItems: 'Total wage items'
        },
        tabs: {
          detail: 'Detail',
          preview: 'Document preview',
          wageItems: 'Wage items'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No labor insurance declarations yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load labor insurance declarations.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      fields: {
        name: {
          label: 'Name',
          required: 'Name is required.',
          tooLong: 'Must be 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Start date is required.'
        },
        removeSelection: 'Remove {{name}}',
        selectedCount: '{{count}} selected',
        sourceRef: {
          placeholder: 'Select source'
        },
        statutory: {
          summary: 'Required statutory fields are missing or unmapped.'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        columns: {
          amount: 'Amount',
          item: 'Item'
        },
        documentTitle: 'Labour Insurance Annual Declaration',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download queued',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        totalWagesLabel: 'Total wages'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Labor insurance declarations',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    standardRemunerationNotifications: {
      add: 'Add standard remuneration notifications',
      columns: {
        name: 'Profile name',
        period: 'Effective period',
        structures: 'Payroll structures'
      },
      description: 'Fixed vs variable wage item classification for standard remuneration notification.',
      editor: {
        createTitle: 'Add standard remuneration notifications',
        discardConfirm: 'Discard changes?',
        discardConfirmCancel: 'Keep editing',
        discardConfirmOk: 'Discard',
        editTitle: 'Edit {{name}}',
        emptyWageItems: 'No wage items selected.',
        mappingColumns: {
          classification: 'Classification',
          column: 'Statutory column',
          field: 'Aggregate field',
          itemCount: 'Included items',
          label: 'Form label',
          payrollItem: 'Payroll item',
          sourceType: 'Data source'
        },
        saveCreate: 'Create profile',
        saveUpdate: 'Save',
        savedToast: 'Saved',
        sections: {
          basic: 'Basic information',
          wageItems: 'Total wage items'
        },
        tabs: {
          classifications: 'Wage classification',
          detail: 'Detail',
          preview: 'Document preview'
        }
      },
      empty: {
        addManually: 'Add manually',
        description: 'No standard remuneration notifications yet.'
      },
      error: {
        retry: 'Reload',
        title: 'Could not load standard remuneration notifications.'
      },
      filters: {
        searchPlaceholder: 'Search by name'
      },
      fields: {
        classifications: {
          exclude: 'Excluded',
          fixed: 'Fixed wage',
          variable: 'Variable wage'
        },
        name: {
          label: 'Name',
          required: 'Name is required.',
          tooLong: 'Must be 64 characters or fewer.'
        },
        payrollStructures: {
          label: 'Payroll structures',
          placeholder: 'Select payroll structures',
          required: 'Select at least one payroll structure.'
        },
        period: {
          label: 'Effective period',
          separator: '–'
        },
        periodEnd: {
          beforeStart: 'End date must be on or after start date.',
          placeholder: 'Select end date'
        },
        periodStart: {
          required: 'Start date is required.'
        },
        sourceRef: {
          placeholder: 'Select source'
        },
        statutory: {
          summary: 'Required statutory fields are missing or unmapped.'
        }
      },
      period: {
        ended: 'Ended',
        future: 'Not started',
        openEnded: '{{start}} –',
        range: '{{start}} – {{end}}'
      },
      preview: {
        columns: {
          amount: 'Amount',
          item: 'Item'
        },
        documentTitle: 'Standard Remuneration Notification',
        download: 'Download PDF',
        downloadToastDescription: 'PDF export is not wired in this mock.',
        downloadToastTitle: 'Download queued',
        sampleNotice: 'Amounts and dates are sample values for layout review.',
        totalWagesLabel: 'Total'
      },
      structures: {
        more: '+{{count}}',
        none: '–'
      },
      title: 'Standard remuneration notifications',
      validation: {
        showOutOfPeriod: 'Show out of effective period'
      }
    },
    title: 'Settings'
  },
  tabs: {
    alerts: 'Alerts',
    contractAndCost: 'Contract & Cost',
    members: 'Workers',
    overview: 'Overview',
    /** The calculation-result ledger; one row is a single payroll run */
    records: 'Records',
    reports: 'Analysis & Reports',
    ruleAndPolicy: 'Rule & Policy',
    settings: 'Settings'
  }
};